import React, { useState } from 'react'
import { 
  Play, 
  FileText, 
  BookOpen, 
  Monitor, 
  Download, 
  ExternalLink, 
  ChevronRight,
  Video,
  PenTool,
  Library,
  Cpu,
  X,
  Search,
  Filter
} from 'lucide-react'
import './MaterialMenu.css'

const MaterialMenu = ({ courseName, isOpen, onClose }) => {
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Mock data para los materiales - en el futuro se puede conectar a una API
  const materials = {
    videos: [
      {
        id: 1,
        title: 'Introducción a la Cinemática',
        description: 'Conceptos fundamentales del movimiento',
        duration: '15:30',
        thumbnail: 'https://via.placeholder.com/300x200/4f46e5/ffffff?text=Video+1',
        url: '#'
      },
      {
        id: 2,
        title: 'Leyes de Newton Explicadas',
        description: 'Análisis detallado de las tres leyes',
        duration: '22:15',
        thumbnail: 'https://via.placeholder.com/300x200/7c3aed/ffffff?text=Video+2',
        url: '#'
      },
      {
        id: 3,
        title: 'Ejercicios de Dinámica',
        description: 'Problemas resueltos paso a paso',
        duration: '18:45',
        thumbnail: 'https://via.placeholder.com/300x200/2563eb/ffffff?text=Video+3',
        url: '#'
      }
    ],
    exercises: [
      {
        id: 1,
        title: 'Problemas de Cinemática',
        description: 'Ejercicios básicos y avanzados',
        type: 'PDF',
        pages: 25,
        difficulty: 'Intermedio',
        url: '#'
      },
      {
        id: 2,
        title: 'Taller de Fuerzas',
        description: 'Ejercicios prácticos sobre fuerzas',
        type: 'PDF',
        pages: 18,
        difficulty: 'Básico',
        url: '#'
      },
      {
        id: 3,
        title: 'Examen de Práctica',
        description: 'Simulacro de examen parcial',
        type: 'Interactive',
        pages: 12,
        difficulty: 'Avanzado',
        url: '#'
      }
    ],
    books: [
      {
        id: 1,
        title: 'Física Universitaria - Sears',
        author: 'Sears, Zemansky',
        description: 'Texto completo de física general',
        pages: 1520,
        format: 'PDF',
        size: '45 MB',
        url: '#'
      },
      {
        id: 2,
        title: 'Mecánica Clásica - Goldstein',
        author: 'Herbert Goldstein',
        description: 'Tratado avanzado de mecánica',
        pages: 638,
        format: 'PDF',
        size: '28 MB',
        url: '#'
      },
      {
        id: 3,
        title: 'Problemas de Física - Koshkin',
        author: 'Koshkin, Shirkevich',
        description: 'Colección de problemas resueltos',
        pages: 415,
        format: 'PDF',
        size: '22 MB',
        url: '#'
      }
    ],
    simulators: [
      {
        id: 1,
        title: 'Simulador de Movimiento',
        description: 'Visualiza trayectorias y velocidades',
        type: 'Web App',
        features: ['Gráficos interactivos', 'Análisis en tiempo real'],
        url: '#'
      },
      {
        id: 2,
        title: 'Laboratorio Virtual de Fuerzas',
        description: 'Experimenta con diferentes fuerzas',
        type: 'Simulation',
        features: ['Experimentos virtuales', 'Mediciones precisas'],
        url: '#'
      },
      {
        id: 3,
        title: 'PhET Physics Simulations',
        description: 'Simulaciones interactivas de física',
        type: 'External',
        features: ['Múltiples experimentos', 'Gratuito'],
        url: 'https://phet.colorado.edu/es/'
      }
    ]
  }

  const categories = [
    {
      id: 'videos',
      title: 'Videos',
      icon: Video,
      description: 'Clases grabadas y tutoriales',
      color: 'from-red-500 to-pink-600',
      count: materials.videos.length
    },
    {
      id: 'exercises',
      title: 'Ejercicios',
      icon: PenTool,
      description: 'Problemas y tareas prácticas',
      color: 'from-blue-500 to-indigo-600',
      count: materials.exercises.length
    },
    {
      id: 'books',
      title: 'Libros',
      icon: Library,
      description: 'Textos y referencias',
      color: 'from-green-500 to-emerald-600',
      count: materials.books.length
    },
    {
      id: 'simulators',
      title: 'Simuladores',
      icon: Cpu,
      description: 'Herramientas interactivas',
      color: 'from-purple-500 to-violet-600',
      count: materials.simulators.length
    }
  ]

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId)
  }

  const renderMaterialItem = (item, type) => {
    switch (type) {
      case 'videos':
        return (
          <div key={item.id} className="material-item video-item">
            <div className="item-thumbnail">
              <img src={item.thumbnail} alt={item.title} />
              <div className="play-overlay">
                <Play size={24} />
              </div>
              <div className="duration-badge">{item.duration}</div>
            </div>
            <div className="item-content">
              <h4>{item.title}</h4>
              <p>{item.description}</p>
              <div className="item-actions">
                <button className="action-btn primary">
                  <Play size={16} />
                  Reproducir
                </button>
                <button className="action-btn secondary">
                  <Download size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      
      case 'exercises':
        return (
          <div key={item.id} className="material-item exercise-item">
            <div className="item-header">
              <div className="item-icon">
                <FileText size={24} />
              </div>
              <div className={`difficulty-badge ${item.difficulty.toLowerCase()}`}>
                {item.difficulty}
              </div>
            </div>
            <div className="item-content">
              <h4>{item.title}</h4>
              <p>{item.description}</p>
              <div className="item-meta">
                <span>{item.pages} páginas</span>
                <span>{item.type}</span>
              </div>
              <div className="item-actions">
                <button className="action-btn primary">
                  <ExternalLink size={16} />
                  Abrir
                </button>
                <button className="action-btn secondary">
                  <Download size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      
      case 'books':
        return (
          <div key={item.id} className="material-item book-item">
            <div className="item-header">
              <div className="item-icon">
                <BookOpen size={24} />
              </div>
              <div className="format-badge">{item.format}</div>
            </div>
            <div className="item-content">
              <h4>{item.title}</h4>
              <p className="author">Por {item.author}</p>
              <p>{item.description}</p>
              <div className="item-meta">
                <span>{item.pages} páginas</span>
                <span>{item.size}</span>
              </div>
              <div className="item-actions">
                <button className="action-btn primary">
                  <ExternalLink size={16} />
                  Leer
                </button>
                <button className="action-btn secondary">
                  <Download size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      
      case 'simulators':
        return (
          <div key={item.id} className="material-item simulator-item">
            <div className="item-header">
              <div className="item-icon">
                <Monitor size={24} />
              </div>
              <div className="type-badge">{item.type}</div>
            </div>
            <div className="item-content">
              <h4>{item.title}</h4>
              <p>{item.description}</p>
              <div className="features-list">
                {item.features.map((feature, index) => (
                  <span key={index} className="feature-tag">{feature}</span>
                ))}
              </div>
              <div className="item-actions">
                <button className="action-btn primary">
                  <ExternalLink size={16} />
                  Usar Simulador
                </button>
              </div>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="material-menu-overlay">
      <div className="material-menu">
        <div className="menu-header">
          <div className="menu-title">
            <BookOpen size={24} />
            <h2>Material de {courseName}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="menu-search">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar material..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="categories-grid">
          {categories.map((category) => {
            const IconComponent = category.icon
            
            return (
              <div 
                key={category.id} 
                className="category-box"
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className={`category-card bg-gradient-to-r ${category.color}`}>
                  <div className="category-icon">
                    <IconComponent size={40} />
                  </div>
                  <div className="category-info">
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>
                    <div className="category-count">
                      {category.count} elementos
                    </div>
                  </div>
                  <div className="category-hover-effect">
                    <ChevronRight size={24} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {activeCategory && (
          <div className="materials-overlay">
            <div className="materials-modal">
              <div className="materials-header">
                <h3>
                  {categories.find(cat => cat.id === activeCategory)?.title}
                </h3>
                <button 
                  className="close-materials-btn"
                  onClick={() => setActiveCategory(null)}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="materials-content">
                <div className="materials-grid">
                  {materials[activeCategory]
                    .filter(item => 
                      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      item.description.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map(item => renderMaterialItem(item, activeCategory))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeCategory === null && (
          <div className="welcome-message">
            <div className="welcome-icon">
              <BookOpen size={48} />
            </div>
            <h3>¡Bienvenido al Material de {courseName}!</h3>
            <p>Selecciona una categoría para explorar el contenido disponible</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default MaterialMenu
